/**
 * Product Bundle Variant Picker
 * Handles swatch-based variant selection and price calculation for bundle products on PDP.
 * Works with the product-bundle-variant-picker snippet.
 *
 * State is keyed by "instance" (a slot's position in custom.bundle_data.products),
 * not by product id, so the same product can appear several times in one bundle
 * with an independent variant selection each time. Variant *data* stays keyed by
 * product id (it is identical for every instance of that product).
 *
 * Each instance may also pin the selectable variants via custom.bundle_data
 * ("variants": [variant ids]); matching and fallbacks stay within that set.
 */
if (!customElements.get('product-bundle-variant-picker')) {
  customElements.define(
    'product-bundle-variant-picker',
    class ProductBundleVariantPicker extends HTMLElement {
      constructor() {
        super();
        this.variantsData = {}; // product_id -> { variant_id: { option1, option2, option3, price, available, ... } }
        this.selectedVariantId = {}; // instance_id -> variant_id
        this.instanceProductId = {}; // instance_id -> product_id
        this.instanceAllowedVariants = {}; // instance_id -> [variant ids] pinned by custom.bundle_data (empty = all)
        this.instanceBundleImages = {}; // instance_id -> { variant_id: composite image url }
        this.formSubmitHandler = null;
      }

      connectedCallback() {
        this.sectionId = this.closest('product-info')?.dataset?.section || '';
        this.expectedItemCount = parseInt(this.dataset.expectedItemCount, 10) || 0;

        // Parse embedded variant data JSON (one block per unique product).
        this.querySelectorAll('[data-bundle-variants-json]').forEach((script) => {
          try {
            const productId = script.dataset.bundleVariantsJson;
            this.variantsData[productId] = JSON.parse(script.textContent);
          } catch (e) {
            console.error('Failed to parse bundle variant data:', e);
          }
        });

        // Per-instance composite image map (instance_id -> { variant_id: url }).
        // The image metafield can differ between instances of the same product.
        this.querySelectorAll('[data-bundle-images-json]').forEach((script) => {
          try {
            this.instanceBundleImages[script.dataset.bundleImagesJson] = JSON.parse(script.textContent);
          } catch (e) {
            console.error('Failed to parse bundle image data:', e);
          }
        });

        // Map each bundle slot (instance) to its product. The same product can
        // appear in more than one instance, each with an independent selection.
        this.querySelectorAll('[data-bundle-item]').forEach((item) => {
          const instanceId = item.dataset.instanceId;
          this.instanceProductId[instanceId] = item.dataset.productId;
          const allowed = item.dataset.allowedVariants;
          if (allowed) {
            this.instanceAllowedVariants[instanceId] = allowed
              .split(',')
              .map((id) => parseInt(id, 10))
              .filter((id) => Number.isFinite(id));
          }
        });

        // Initialize selected variant IDs from hidden inputs and checked radios
        this.initSelectedVariants();

        this.bindEvents();
        this.updateAllPrices();
        this.enableAddToCart();
        this.initComposite();
      }

      disconnectedCallback() {
        const form = this.getProductForm();
        if (form && this.formSubmitHandler) {
          form.removeEventListener('submit', this.formSubmitHandler);
        }
        if (this._onCompositeResize) {
          window.removeEventListener('resize', this._onCompositeResize);
        }
      }

      initSelectedVariants() {
        // For hidden inputs (products with no variants)
        this.querySelectorAll('[data-bundle-variant-hidden]').forEach((input) => {
          this.selectedVariantId[input.dataset.instanceId] = parseInt(input.dataset.variantId, 10);
        });

        // For each bundle slot with variant options, find the matching variant
        this.querySelectorAll('[data-bundle-item]').forEach((item) => {
          const instanceId = item.dataset.instanceId;
          const productId = item.dataset.productId;
          if (this.selectedVariantId[instanceId]) return; // Already set from hidden input

          const selectedOptions = this.getSelectedOptions(instanceId);
          if (Object.keys(selectedOptions).length > 0) {
            const variant = this.findMatchingVariant(productId, selectedOptions, instanceId);
            if (variant) {
              this.selectedVariantId[instanceId] = variant.id;
            }
          }

          // Fallback: first available variant
          if (!this.selectedVariantId[instanceId]) {
            const data = this.variantsData[productId];
            if (data) {
              for (const [vid, v] of Object.entries(data)) {
                if (v.available && this.isVariantAllowed(instanceId, vid)) {
                  this.selectedVariantId[instanceId] = parseInt(vid, 10);
                  break;
                }
              }
            }
          }
        });
      }

      bindEvents() {
        // Listen for change on all radio inputs (swatches/buttons) inside this element
        this.addEventListener('change', (e) => {
          const input = e.target;
          if (input.type === 'radio' && input.dataset.instanceId) {
            this.onSwatchChange(input);
          }
        });

        // Also handle dropdown selects (fallback)
        this.addEventListener('change', (e) => {
          const select = e.target;
          if (select.tagName === 'SELECT' && select.classList.contains('bundle-variant-select')) {
            this.onSelectChange(select);
          }
        });

        // Intercept form submit for bundle multi-item cart add
        const form = this.getProductForm();
        if (form && form.dataset.isBundle === 'true') {
          this.formSubmitHandler = this.handleFormSubmit.bind(this);
          form.addEventListener('submit', this.formSubmitHandler);
        }
      }

      getProductForm() {
        return document.getElementById(`product-form-${this.sectionId}`);
      }

      /**
       * Get currently selected option values for a bundle slot from checked radio inputs
       */
      getSelectedOptions(instanceId) {
        const options = {};
        this.querySelectorAll(
          `input[type="radio"][data-instance-id="${instanceId}"]:checked`
        ).forEach((radio) => {
          const pos = parseInt(radio.dataset.optionPosition, 10);
          options[pos] = radio.dataset.optionValue;
        });
        return options;
      }

      /**
       * Find matching variant given selected option values
       */
      isVariantAllowed(instanceId, variantId) {
        const allowed = this.instanceAllowedVariants[instanceId];
        if (!allowed || allowed.length === 0) return true;
        return allowed.includes(parseInt(variantId, 10));
      }

      /**
       * Find matching variant given selected option values, restricted to the
       * variants pinned by custom.bundle_data for this instance (when provided).
       */
      findMatchingVariant(productId, selectedOptions, instanceId) {
        const data = this.variantsData[productId];
        if (!data) return null;

        // Exact match first
        for (const [vid, variant] of Object.entries(data)) {
          if (!this.isVariantAllowed(instanceId, vid)) continue;
          let match = true;
          for (const [pos, val] of Object.entries(selectedOptions)) {
            const optKey = 'option' + pos;
            if (variant[optKey] !== val) {
              match = false;
              break;
            }
          }
          if (match) return variant;
        }

        // Partial match: try matching with just option1
        if (selectedOptions[1]) {
          for (const [vid, variant] of Object.entries(data)) {
            if (
              this.isVariantAllowed(instanceId, vid) &&
              variant.option1 === selectedOptions[1] &&
              variant.available
            ) {
              return variant;
            }
          }
        }

        return null;
      }

      onSwatchChange(input) {
        const instanceId = input.dataset.instanceId;
        const productId = input.dataset.productId;

        const selectedOptions = this.getSelectedOptions(instanceId);

        if (Object.keys(selectedOptions).length === 0) return;

        const variant = this.findMatchingVariant(productId, selectedOptions, instanceId);
        if (variant) {
          this.selectedVariantId[instanceId] = variant.id;
          this.updateItemVariantName(instanceId, variant);
          this.updateItemPrice(instanceId, variant);
          this.updateItemImage(instanceId, variant);
          this.updateTotalPrice();
          this.updateMainPrice();
          this.enableAddToCart();
          this.activateComposite();
        }
      }

      onSelectChange(select) {
        const instanceId = select.dataset.instanceId;
        const productId = select.dataset.productId;
        const data = this.variantsData[productId];
        if (!data) return;

        // Collect all selected dropdown values for this slot
        const dropdowns = this.querySelectorAll(
          `.bundle-variant-select[data-instance-id="${instanceId}"]`
        );
        const selectedOptions = {};
        dropdowns.forEach((dd) => {
          const opt = dd.options[dd.selectedIndex];
          if (opt && opt.dataset.optionPosition) {
            selectedOptions[parseInt(opt.dataset.optionPosition, 10)] = opt.dataset.optionValue;
          }
        });

        if (Object.keys(selectedOptions).length > 0) {
          const variant = this.findMatchingVariant(productId, selectedOptions, instanceId);
          if (variant) {
            this.selectedVariantId[instanceId] = variant.id;
            this.updateItemVariantName(instanceId, variant);
            this.updateItemPrice(instanceId, variant);
            this.updateItemImage(instanceId, variant);
            this.updateTotalPrice();
            this.updateMainPrice();
            this.enableAddToCart();
            this.activateComposite();
            return;
          }
        }

        // Simple dropdown (single-option product): use the selected value directly
        const selectedOption = select.selectedOptions[0];
        if (selectedOption) {
          for (const [vid, variant] of Object.entries(data)) {
            if (
              this.isVariantAllowed(instanceId, vid) &&
              variant.option1 === selectedOption.value &&
              variant.available
            ) {
              this.selectedVariantId[instanceId] = variant.id;
              this.updateItemVariantName(instanceId, variant);
              this.updateItemPrice(instanceId, variant);
              this.updateItemImage(instanceId, variant);
              this.updateTotalPrice();
              this.updateMainPrice();
              this.enableAddToCart();
              this.activateComposite();
              break;
            }
          }
        }
      }

      /**
       * Show the selected variant's name under the product title.
       */
      updateItemVariantName(instanceId, variant) {
        const el = this.querySelector(
          `[data-bundle-item-variant][data-instance-id="${instanceId}"]`
        );
        if (el && variant && variant.title) {
          el.textContent = variant.title;
        }
      }

      updateItemPrice(instanceId, variant) {
        const priceEl = this.querySelector(
          `[data-bundle-item-price][data-instance-id="${instanceId}"]`
        );
        if (priceEl) {
          priceEl.textContent = this.formatMoney(variant.price);
        }
      }

      /**
       * Swap the bundle slot's thumbnail to the selected variant's image.
       */
      updateItemImage(instanceId, variant) {
        if (!variant || !variant.featured_image || !variant.featured_image.src) return;
        const wrapper = this.querySelector(
          `[data-bundle-item-image][data-instance-id="${instanceId}"]`
        );
        if (!wrapper) return;
        const img = wrapper.querySelector('img');
        if (!img) return;

        let src = variant.featured_image.src;
        src = src + (src.indexOf('?') !== -1 ? '&' : '?') + 'width=200';
        img.removeAttribute('srcset');
        img.removeAttribute('data-srcset');
        img.src = src;
      }

      updateAllPrices() {
        // Update each slot price from its selected variant
        Object.entries(this.selectedVariantId).forEach(([instanceId, variantId]) => {
          const data = this.variantsData[this.instanceProductId[instanceId]];
          if (data && data[variantId]) {
            this.updateItemVariantName(instanceId, data[variantId]);
            this.updateItemPrice(instanceId, data[variantId]);
          }
        });
        this.updateTotalPrice();
        this.updateMainPrice();
      }

      getTotalPrice() {
        let totalPrice = 0;
        let totalCompareAtPrice = 0;

        Object.entries(this.selectedVariantId).forEach(([instanceId, variantId]) => {
          const data = this.variantsData[this.instanceProductId[instanceId]];
          if (data && data[variantId]) {
            const v = data[variantId];
            totalPrice += v.price || 0;
            totalCompareAtPrice += v.compare_at_price || v.price || 0;
          }
        });

        return { totalPrice, totalCompareAtPrice };
      }

      updateTotalPrice() {
        const { totalPrice } = this.getTotalPrice();
        const totalEl = this.querySelector('[data-bundle-total-price]');
        if (totalEl) {
          totalEl.textContent = this.formatMoney(totalPrice);
        }
      }

      updateMainPrice() {
        const { totalPrice, totalCompareAtPrice } = this.getTotalPrice();
        const priceEl = document.getElementById(`price-${this.sectionId}`);
        if (!priceEl) return;

        const fPrice =
          priceEl.querySelector('.f-price') || priceEl.closest('.f-price');
        const regularPriceEl = priceEl.querySelector('.f-price-item--regular');
        const salePriceEl = priceEl.querySelector(
          '.f-price__sale .f-price-item--sale'
        );
        const comparePriceEl = priceEl.querySelector(
          '.f-price__sale .f-price-item--regular s'
        );

        if (regularPriceEl) {
          regularPriceEl.textContent = this.formatMoney(totalPrice);
        }

        if (fPrice) {
          if (totalCompareAtPrice > totalPrice) {
            fPrice.classList.add('f-price--on-sale');
            if (salePriceEl) {
              salePriceEl.textContent = this.formatMoney(totalPrice);
            }
            if (comparePriceEl) {
              comparePriceEl.textContent = this.formatMoney(totalCompareAtPrice);
            }
          } else {
            fPrice.classList.remove('f-price--on-sale');
          }
        }
      }

      getAllVariantIds() {
        return Object.entries(this.selectedVariantId).map(([instanceId, variantId]) => ({
          id: variantId,
          instance_id: instanceId,
          product_id: this.instanceProductId[instanceId],
        }));
      }

      areAllVariantsAvailable() {
        const bundleItems = this.querySelectorAll('[data-bundle-item]');
        const selections = Object.entries(this.selectedVariantId);
        const expectedItemCount = this.expectedItemCount || bundleItems.length;

        // Do not allow an incomplete bundle when a configured child product
        // cannot be resolved and was therefore skipped by Liquid.
        if (
          bundleItems.length !== expectedItemCount ||
          selections.length !== expectedItemCount
        ) {
          return false;
        }

        return selections.every(
          ([instanceId, variantId]) => {
            const data = this.variantsData[this.instanceProductId[instanceId]];
            return data && data[variantId] && data[variantId].available;
          }
        );
      }

      enableAddToCart() {
        const form = this.getProductForm();
        if (!form) return;

        const addButton = form.querySelector('[name="add"]');
        const addButtonText = form.querySelector('[name="add"] > span');
        const allAvailable = this.areAllVariantsAvailable();

        if (addButton) {
          if (allAvailable) {
            addButton.removeAttribute('disabled');
            if (addButtonText) {
              addButtonText.textContent = this.decodeHtml(
                FoxTheme.variantStrings?.addAllToCart || 'Add all to cart'
              );
            }
          } else {
            addButton.setAttribute('disabled', 'disabled');
            if (addButtonText) {
              addButtonText.textContent = this.decodeHtml(
                FoxTheme.variantStrings?.soldOut || 'Sold out'
              );
            }
          }
        }
      }

      // ----- Bundle "assembled image" composite preview -----

      /**
       * Stack each bundle slot's composite PNG (from that item's image metafield,
       * all 100% overlapping) into one assembled image, shown over the gallery when
       * a color swatch is clicked. A close button (top-right) dismisses it.
       */
      initComposite() {
        // Desktop (pointer) devices only: the assembled image covers the gallery.
        if (!window.matchMedia('(hover: hover)').matches) return;

        const productInfo = this.closest('product-info');
        // PDP-only feature: skip the assembled-image overlay in the quick-view drawer.
        if (productInfo?.dataset.viewMode === 'quick-view') return;
        this.galleryViewer = productInfo?.querySelector('.product__media-gallery-viewer');
        const viewerWrap = productInfo?.querySelector('.product__media-gallery-viewer-wrap');
        if (!this.galleryViewer || !viewerWrap) return;

        const itemEls = Array.from(this.querySelectorAll('[data-bundle-item]'));
        this.bundleItemOrder = itemEls.map((item) => item.dataset.instanceId);

        // Nothing to assemble if no instance carries a bundle image.
        const hasBundleImage = Object.values(this.instanceBundleImages).some((map) =>
          Object.values(map).some((url) => url)
        );
        if (!hasBundleImage) return;

        this.composite = document.createElement('div');
        this.composite.className = 'bundle-composite-overlay';
        this.composite.setAttribute('aria-hidden', 'true');
        this.composite.setAttribute('aria-busy', 'false');
        viewerWrap.appendChild(this.composite);

        // One stable layer per bundle slot. The z-order comes from each item's
        // "index" (higher = on top), falling back to DOM order. Each layer
        // crossfades to its new image when the selected/previewed color changes.
        this.compositeSlots = itemEls.map((item, i) => {
          const el = document.createElement('div');
          el.className = 'bundle-composite-overlay__layer';
          const stackIndex = parseInt(item.dataset.stackIndex, 10);
          el.style.zIndex = String(Number.isFinite(stackIndex) ? stackIndex : i + 1);
          this.composite.appendChild(el);
          return { instanceId: item.dataset.instanceId, el, url: null, img: null };
        });

        // A subtle loading indicator bridges the delay while the new stack is
        // downloading. Existing layers remain visible underneath on later swaps.
        const loader = document.createElement('div');
        loader.className = 'bundle-composite-overlay__loader';
        loader.setAttribute('aria-hidden', 'true');
        loader.innerHTML = '<span></span>';
        this.composite.appendChild(loader);

        // Keep the overlay aligned to the gallery while it is on screen.
        this._onCompositeResize = () => {
          if (this.composite && this.composite.classList.contains('is-visible')) {
            this.positionComposite();
          }
        };
        window.addEventListener('resize', this._onCompositeResize);

        // Close button (top-right) to dismiss the assembled image. The overlay
        // is pointer-events:none so it never blocks the gallery; only this button
        // re-enables pointer events (see CSS).
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'bundle-composite-overlay__close';
        closeBtn.setAttribute('aria-label', this.dataset.closeLabel || 'Close');
        closeBtn.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6 18 18M18 6 6 18"/></svg>';
        closeBtn.addEventListener('click', () => this.hideComposite());
        this.composite.appendChild(closeBtn);
      }

      hideComposite() {
        if (this.composite) {
          this.composite.classList.remove('is-visible', 'is-loading');
          this.composite.setAttribute('aria-busy', 'false');
        }
      }

      // Show/update the assembled image for the current selection (on swatch click).
      activateComposite() {
        this.showSelectedComposite();
      }

      showSelectedComposite() {
        if (!this.composite) return;
        const layers = this.buildCompositeLayers();
        if (!layers) return;
        const pendingImages = this.renderCompositeLayers(layers);
        this.positionComposite();
        this.composite.classList.add('is-visible');

        const loadId = (this.compositeLoadId || 0) + 1;
        this.compositeLoadId = loadId;

        if (pendingImages.length === 0) {
          this.composite.classList.remove('is-loading');
          this.composite.setAttribute('aria-busy', 'false');
          return;
        }

        this.composite.classList.add('is-loading');
        this.composite.setAttribute('aria-busy', 'true');
        Promise.allSettled(pendingImages).then(() => {
          if (!this.composite || this.compositeLoadId !== loadId) return;
          this.composite.classList.remove('is-loading');
          this.composite.setAttribute('aria-busy', 'false');
        });
      }

      /**
       * One bundle image per slot (instance_id -> url), each using that slot's
       * current selection. Returns null if no slot has an image.
       */
      buildCompositeLayers() {
        const byInstance = {};
        let hasAny = false;
        this.bundleItemOrder.forEach((instanceId) => {
          const url = this.resolveBundleImage(instanceId);
          byInstance[instanceId] = url || null;
          if (url) hasAny = true;
        });
        return hasAny ? byInstance : null;
      }

      /**
       * Resolve a slot's bundle image from its current variant selection, falling
       * back to the stored selection, then any variant that has an image.
       */
      resolveBundleImage(instanceId) {
        const productId = this.instanceProductId[instanceId];
        const data = this.variantsData[productId];
        const images = this.instanceBundleImages[instanceId] || {};
        if (!data) return null;

        const variant = this.findMatchingVariant(productId, this.getSelectedOptions(instanceId), instanceId);
        if (variant && images[variant.id]) return images[variant.id];

        const selectedId = this.selectedVariantId[instanceId];
        if (selectedId && images[selectedId]) return images[selectedId];

        const anyId = Object.keys(images).find(
          (vid) => images[vid] && this.isVariantAllowed(instanceId, vid)
        );
        return anyId ? images[anyId] : null;
      }

      renderCompositeLayers(byInstance) {
        const pendingImages = [];
        this.compositeSlots.forEach((slot) => {
          const pendingImage = this.setSlotImage(
            slot,
            byInstance[slot.instanceId] || null
          );
          if (pendingImage) pendingImages.push(pendingImage);
        });
        return pendingImages;
      }

      // Crossfade a single layer to a new image (or fade it out when url is null).
      setSlotImage(slot, url) {
        if (slot.url === url) return null;
        slot.url = url;

        const prev = slot.img;
        if (!url) {
          slot.img = null;
          if (prev) this.fadeOutAndRemove(prev);
          return null;
        }

        const next = document.createElement('img');
        next.className = 'bundle-composite-overlay__img';
        next.alt = '';
        next.decoding = 'async';
        slot.img = next;
        slot.el.appendChild(next);

        return new Promise((resolve) => {
          let revealed = false;
          const reveal = () => {
            if (revealed) return;
            revealed = true;
            // Next frame so the opacity transition actually runs.
            requestAnimationFrame(() => next.classList.add('is-shown'));
            if (prev) this.fadeOutAndRemove(prev);
            resolve();
          };
          next.addEventListener('load', reveal, { once: true });
          next.addEventListener('error', reveal, { once: true });
          next.src = url;
          if (next.complete) reveal();
        });
      }

      fadeOutAndRemove(img) {
        img.classList.remove('is-shown');
        const remove = () => img.remove();
        img.addEventListener('transitionend', remove, { once: true });
        setTimeout(remove, 500); // fallback if transitionend doesn't fire
      }

      // Match the overlay box to the gallery's main image area.
      positionComposite() {
        const v = this.galleryViewer;
        if (!v || !this.composite) return;
        this.composite.style.top = `${v.offsetTop}px`;
        this.composite.style.left = `${v.offsetLeft}px`;
        this.composite.style.width = `${v.offsetWidth}px`;
        this.composite.style.height = `${v.offsetHeight}px`;
      }

      handleFormSubmit(event) {
        event.preventDefault();

        const variantIds = this.getAllVariantIds();
        if (!this.areAllVariantsAvailable()) {
          this.enableAddToCart();
          return;
        }

        const form = event.target;
        const submitButton = form.querySelector('[name="add"]');
        const errorWrapper = form.querySelector(
          '.product-form__error-message-wrapper'
        );
        const errorMessage = form.querySelector('.product-form__error-message');
        const cartDrawer = document.querySelector('cart-drawer');

        // Ask the cart drawer (and any other listeners) which sections to
        // re-render server-side, so the drawer reflects the new items.
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        // Repeated variant ids (the same product/variant in multiple slots) are
        // merged into a single line item with the combined quantity.
        const quantities = {};
        variantIds.forEach((v) => {
          quantities[v.id] = (quantities[v.id] || 0) + 1;
        });

        const body = {
          items: Object.entries(quantities).map(([id, quantity]) => ({
            id: parseInt(id, 10),
            quantity,
          })),
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname,
        };

        if (submitButton) {
          submitButton.classList.add('btn--loading');
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.setAttribute('disabled', 'disabled');
        }

        if (errorWrapper) errorWrapper.hidden = true;

        fetch(`${FoxTheme.routes.cart_add_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then(async (parsedState) => {
            if (parsedState.status) {
              if (errorWrapper) {
                errorWrapper.hidden = false;
                if (errorMessage) {
                  errorMessage.textContent =
                    parsedState.description || parsedState.message;
                }
              }
              return;
            }

            document.dispatchEvent(
              new CustomEvent('product-ajax:added', {
                detail: { product: parsedState },
              })
            );

            // No drawer available (or cart is a page) → go to the cart page.
            if (!cartDrawer || FoxTheme.settings?.cartType === 'page') {
              window.location = FoxTheme.routes.cart_url;
              return;
            }

            // Pull the full cart for accurate counts, attach the rendered
            // sections, then let the cart drawer re-render and open.
            const cartJson = await (
              await fetch(`${FoxTheme.routes.cart_url}`, {
                ...FoxTheme.utils.fetchConfig(),
              })
            ).json();
            cartJson['sections'] = parsedState['sections'];

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-bundle-variant-picker',
              productVariantId: variantIds.map((v) => v.id).join(','),
              cart: cartJson,
            });

            if (typeof cartDrawer.show === 'function') {
              cartDrawer.show(submitButton);
            }
          })
          .catch((error) => {
            console.error('Bundle add to cart error:', error);
            if (errorWrapper) {
              errorWrapper.hidden = false;
              if (errorMessage) {
                errorMessage.textContent =
                  (window.FoxTheme &&
                    FoxTheme.cartStrings &&
                    FoxTheme.cartStrings.error) ||
                  'There was an error adding items to your cart.';
              }
            }
          })
          .finally(() => {
            if (submitButton) {
              submitButton.classList.remove('btn--loading');
              submitButton.removeAttribute('aria-disabled');
            }
            this.enableAddToCart();
          });
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(
            cents,
            (window.FoxTheme?.settings?.moneyFormat || '').replace(
              'amount_with_comma_separator',
              '{{amount}}'
            )
          );
        }
        return '$' + (cents / 100).toFixed(2);
      }

      decodeHtml(html) {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
      }
    }
  );
}
